import React from 'react';
import { User, Users, Clock, AlertTriangle, DollarSign, FileText, X } from 'lucide-react';
import { LTSSService, Alert } from '../../types/ltss';
import { formatCurrency } from '../../utils/timeUtils';

interface SearchResultsProps {
  query: string;
  searchType: 'staff' | 'client' | null;
  services: LTSSService[];
  alerts: Alert[];
  onClose: () => void;
}

export function SearchResults({ query, searchType, services, alerts, onClose }: SearchResultsProps) {
  // Filter services based on search
  const filteredServices = services.filter(s => {
    const searchLower = query.toLowerCase();
    if (searchType === 'staff') {
      return s.staffName?.toLowerCase().includes(searchLower);
    } else if (searchType === 'client') {
      return s.clientName?.toLowerCase().includes(searchLower);
    }
    // Search both if no type specified
    return s.staffName?.toLowerCase().includes(searchLower) || 
           s.clientName?.toLowerCase().includes(searchLower);
  });

  // Filter alerts
  const filteredAlerts = alerts.filter(a => {
    const searchLower = query.toLowerCase();
    if (searchType === 'staff') {
      return a.staffName?.toLowerCase().includes(searchLower);
    } else if (searchType === 'client') {
      return a.clientName?.toLowerCase().includes(searchLower);
    }
    return a.staffName?.toLowerCase().includes(searchLower) || 
           a.clientName?.toLowerCase().includes(searchLower);
  });

  // Calculate stats
  const totalHours = filteredServices.reduce((sum, s) => sum + (s.serviceLength || 0), 0);
  const totalRevenue = filteredServices.reduce((sum, s) => sum + (s.amountPaid || 0), 0);
  const uniqueStaff = new Set(filteredServices.map(s => s.staffName).filter(Boolean));
  const uniqueClients = new Set(filteredServices.map(s => s.clientName).filter(Boolean));

  // Group services by date
  const servicesByDate = filteredServices.reduce((acc, service) => {
    const date = service.serviceDate 
      ? new Date(service.serviceDate).toLocaleDateString() 
      : service.dayOfService || 'Unknown';
    if (!acc[date]) acc[date] = [];
    acc[date].push(service);
    return acc;
  }, {} as Record<string, LTSSService[]>);

  const sortedDates = Object.keys(servicesByDate).sort((a, b) => {
    const dateA = new Date(a);
    const dateB = new Date(b);
    return dateB.getTime() - dateA.getTime();
  });

  return (
    <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-slate-700 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
            searchType === 'staff' 
              ? 'bg-blue-500/20 text-blue-400' 
              : searchType === 'client'
              ? 'bg-green-500/20 text-green-400'
              : 'bg-purple-500/20 text-purple-400'
          }`}>
            {searchType === 'staff' ? <User className="w-5 h-5" /> : 
             searchType === 'client' ? <Users className="w-5 h-5" /> :
             <FileText className="w-5 h-5" />}
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-200">{query}</h2>
            <p className="text-sm text-slate-400">
              {searchType === 'staff' ? 'Staff Member' : 
               searchType === 'client' ? 'Client' : 'Search Results'}
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-2 text-slate-400 hover:text-slate-200 hover:bg-slate-700 
                   rounded-lg transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 border-b border-slate-700">
        <StatCard
          icon={FileText}
          label="Services"
          value={filteredServices.length.toString()}
          color="blue"
        />
        <StatCard
          icon={Clock}
          label="Total Hours"
          value={totalHours.toFixed(1)}
          color="green"
        />
        <StatCard
          icon={DollarSign}
          label="Revenue"
          value={formatCurrency(totalRevenue)}
          color="emerald"
        />
        <StatCard
          icon={AlertTriangle}
          label="Alerts"
          value={filteredAlerts.length.toString()}
          color={filteredAlerts.length > 0 ? 'red' : 'slate'}
        />
      </div>

      {/* Related Info */}
      {searchType === 'staff' && uniqueClients.size > 0 && (
        <div className="px-4 py-3 border-b border-slate-700 bg-slate-800/30">
          <p className="text-sm text-slate-400">
            <span className="font-medium text-slate-300">Clients served:</span>{' '}
            {Array.from(uniqueClients).slice(0, 5).join(', ')}
            {uniqueClients.size > 5 && ` +${uniqueClients.size - 5} more`}
          </p>
        </div>
      )}
      {searchType === 'client' && uniqueStaff.size > 0 && (
        <div className="px-4 py-3 border-b border-slate-700 bg-slate-800/30">
          <p className="text-sm text-slate-400">
            <span className="font-medium text-slate-300">Staff assigned:</span>{' '}
            {Array.from(uniqueStaff).slice(0, 5).join(', ')}
            {uniqueStaff.size > 5 && ` +${uniqueStaff.size - 5} more`}
          </p>
        </div>
      )}

      {/* Alerts Section */}
      {filteredAlerts.length > 0 && (
        <div className="p-4 border-b border-slate-700">
          <h3 className="text-sm font-medium text-slate-300 mb-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-yellow-500" />
            Alerts ({filteredAlerts.length})
          </h3>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {filteredAlerts.slice(0, 10).map((alert, i) => (
              <div
                key={alert.id || i}
                className={`p-3 rounded-lg text-sm ${
                  alert.type === 'CRITICAL' ? 'bg-red-500/10 border border-red-500/30' :
                  alert.type === 'WARNING' ? 'bg-yellow-500/10 border border-yellow-500/30' :
                  'bg-slate-700/50'
                }`}
              >
                <div className="flex items-start gap-2">
                  <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                    alert.type === 'CRITICAL' ? 'bg-red-500 text-white' :
                    alert.type === 'WARNING' ? 'bg-yellow-500 text-black' :
                    'bg-slate-600 text-slate-200'
                  }`}>
                    {alert.type}
                  </span>
                  <p className="text-slate-300 flex-1">{alert.message}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Services Timeline */}
      <div className="p-4">
        <h3 className="text-sm font-medium text-slate-300 mb-3 flex items-center gap-2">
          <Clock className="w-4 h-4 text-blue-400" />
          Service History
        </h3>
        <div className="space-y-4 max-h-96 overflow-y-auto">
          {sortedDates.slice(0, 10).map(date => (
            <div key={date}>
              <p className="text-xs font-medium text-slate-500 mb-2">{date}</p>
              <div className="space-y-2">
                {servicesByDate[date].slice(0, 5).map((service, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-3 p-3 bg-slate-700/30 rounded-lg"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-200 truncate">
                        {searchType === 'staff' ? service.clientName : service.staffName}
                      </p>
                      <p className="text-xs text-slate-500">
                        {service.startTime} - {service.endTime} • {service.serviceLength?.toFixed(1) || '?'} hrs
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-slate-300">
                        {service.amountPaid ? formatCurrency(service.amountPaid) : '-'}
                      </p>
                      <p className={`text-xs ${
                        service.serviceStatus?.includes('Paid') ? 'text-green-400' :
                        service.serviceStatus?.includes('Pending') ? 'text-yellow-400' :
                        'text-slate-500'
                      }`}>
                        {service.serviceStatus || service.claimHistory || '-'}
                      </p>
                    </div>
                  </div>
                ))}
                {servicesByDate[date].length > 5 && (
                  <p className="text-xs text-slate-500 text-center py-1">
                    +{servicesByDate[date].length - 5} more services
                  </p>
                )}
              </div>
            </div>
          ))}
          {sortedDates.length > 10 && (
            <p className="text-sm text-slate-500 text-center py-2">
              Showing 10 of {sortedDates.length} days
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ 
  icon: Icon, 
  label, 
  value, 
  color 
}: { 
  icon: React.ElementType; 
  label: string; 
  value: string; 
  color: string;
}) {
  const colorClasses: Record<string, string> = {
    blue: 'bg-blue-500/20 text-blue-400',
    green: 'bg-green-500/20 text-green-400',
    emerald: 'bg-emerald-500/20 text-emerald-400',
    red: 'bg-red-500/20 text-red-400',
    slate: 'bg-slate-700 text-slate-400',
  };

  return (
    <div className="bg-slate-800/50 rounded-lg p-3">
      <div className="flex items-center gap-2 mb-1">
        <div className={`w-6 h-6 rounded flex items-center justify-center ${colorClasses[color]}`}>
          <Icon className="w-3.5 h-3.5" />
        </div>
        <span className="text-xs text-slate-500">{label}</span>
      </div>
      <p className="text-lg font-semibold text-slate-200">{value}</p>
    </div>
  );
}
